import { bufferToHexString } from '@iotize/common/byte-converter';
import { CodeError } from '@iotize/common/error';
import { ComProtocol } from '@iotize/tap/protocol/api';
import { Subscription } from 'rxjs';

import { debug } from './debug';

const TAG = 'Relay';
export class ComRelayError extends CodeError<ComRelayError.Code> {
  public static illegalArgument(message: string) {
    return new ComRelayError(ComRelayError.Code.IllegalArgmentError, message);
  }

  constructor(code: ComRelayError.Code, msg: string) {
    super(msg, code);
  }
}

export namespace ComRelayError {
  export enum Code {
    IllegalArgmentError = 'IllegalArgmentError',
  }
}

/**
 * This is an experimental feature.
 *
 * @experimental
 */
export class ComRelay {
  protected _sourceProtocolSubscription?: Subscription;

  protected mSourceProtocol?: ComProtocol;
  protected get sourceProtocol(): ComProtocol {
    if (!this.mSourceProtocol) {
      throw ComRelayError.illegalArgument('Missing source protocol');
    }
    return this.mSourceProtocol;
  }

  protected mTargetProtocol?: ComProtocol;
  protected get targetProtocol(): ComProtocol {
    if (!this.mTargetProtocol) {
      throw ComRelayError.illegalArgument('Missing target protocol');
    }
    return this.mTargetProtocol;
  }

  // private IRelayCallback mCallback;
  // public final RelayServiceInfoModel mRelayInfo;

  // private DefaultSocketServer mSocketServer;
  // private WebSocketServer mWebSocketServer;

  /**
   * @param sourceProtocol protocol that will received commands
   * @param targetProtocol protocol on wich commmand will be sent
   */
  constructor(sourceProtocol?: ComProtocol, targetProtocol?: ComProtocol) {
    if (sourceProtocol) {
      this.setSourceProtocol(sourceProtocol);
    }
    if (targetProtocol) {
      this.setTargetProtocol(targetProtocol);
    }
  }

  public setSourceProtocol(protocol: ComProtocol): this {
    if (this.mSourceProtocol) {
      this.removeSourceProtocol();
    }
    this.mSourceProtocol = protocol;
    return this;
  }

  public removeSourceProtocol(): this {
    if (this._sourceProtocolSubscription) {
      this._sourceProtocolSubscription.unsubscribe();
      this._sourceProtocolSubscription = undefined;
    }
    this.mSourceProtocol = undefined;
    return this;
  }

  public setTargetProtocol(protocol: ComProtocol): this {
    this.mTargetProtocol = protocol;
    return this;
  }

  public async start(options?: { timeout?: number }) {
    return promiseTimout<void>(
      options ? options.timeout || -1 : -1,
      async (resolve, reject) => {
        try {
          if (!this.sourceProtocol.isConnected()) {
            debug(TAG, 'Connecting to source protocol...');
            await this.sourceProtocol.connect().toPromise();
          }
          if (!this.targetProtocol.isConnected()) {
            debug(TAG, 'Connecting to target protocol...');
            await this.targetProtocol.connect().toPromise();
          }
          debug(
            TAG,
            'Source/Target are now connected. Listening to messages...',
          );
          // We need to reconnect
          this.listenToDataStream();
          resolve();
        } catch (ex) {
          reject(ex);
        }
      },
    );
  }

  /**
   * Stop relay and disconnect target and source protocol according to given options
   * @param options
   */
  stop(options?: {
    /**
     * true if you don't want that ComRelay disconnect the target protocol
     */
    keepTargetProtocolConnected?: boolean;
    /**
     * true if you don't want that ComRelay disconnect the source protocol
     */
    keepSourceProtocolConnected?: boolean;
  }): void {
    if (
      !options?.keepTargetProtocolConnected &&
      this.mTargetProtocol?.isConnected()
    ) {
      this.mTargetProtocol?.disconnect();
    }
    if (
      !options?.keepSourceProtocolConnected &&
      this.mSourceProtocol?.isConnected()
    ) {
      this.mSourceProtocol?.disconnect();
    }
  }

  listenToDataStream(): Promise<void> {
    // if (!this.mSourceProtocol!.isConnected()){
    //     await this.mSourceProtocol!.connect();
    // }
    return new Promise<void>((resolve, reject) => {
      if (!this.mSourceProtocol) {
        return reject(ComRelayError.illegalArgument(`Missing source protocol`));
      }
      this._sourceProtocolSubscription = this.mSourceProtocol
        .receiveStream()
        .subscribe({
          next: (data: Uint8Array) => {
            if (!data) {
              debug(TAG, 'Received null event...');
              return;
            }
            if (!this.mTargetProtocol) {
              debug(
                TAG,
                'Target protocol is null. Cannot redirect message to it',
              );
              return;
            }
            try {
              debug(TAG, `Received new message`, bufferToHexString(data));
              this.mTargetProtocol
                .send(data)
                .toPromise()
                .then((response) => {
                  response = response || new Uint8Array();
                  debug(
                    TAG,
                    `Target protocol responded with: `,
                    bufferToHexString(response),
                  );
                  return this.sourceProtocol.write(response);
                })
                .then(() => {
                  debug(TAG, `Successfully sent response!`);
                })
                .catch((err: Error) => {
                  debug(
                    TAG,
                    `Cannot transfer response to source protocol: ${err} JSON=${JSON.stringify(
                      err,
                    )}`,
                    err,
                  );
                });
            } catch (err) {
              debug(
                TAG,
                `Source cannot transfer data to target due to error: ${err}`,
                err,
              );
            }
          },
          error: (err: any) => {
            debug(TAG, `Error occured ${err}`);
          },
          complete: () => {
            resolve();
          },
        });
    });
  }
  public onStop() {
    // TODO cleanup
    return this;
  }
}

function promiseTimout<T>(
  ms: number,
  callback: (
    resolve: (value: T | PromiseLike<T>) => void,
    reject: (reason?: any) => void,
  ) => any,
): Promise<T> {
  return new Promise<T>(function (resolve, reject) {
    debug(TAG, `promiseTimout start with ${ms} milliseconds`);
    // Set up the real work
    callback(resolve, reject);

    // Set up the timeout
    if (ms >= 0) {
      setTimeout(function () {
        reject('Promise timed out after ' + ms + ' ms');
      }, ms);
    }
  });
}
